"use client";

import type { ReactNode } from "react";
import type { StreamingResponse, ProviderInfo } from "@/app/lib/types";
import ResponseCard from "./ResponseCard";

interface ResponseGridProps {
  responses: Record<string, StreamingResponse>;
  providerOrder: string[];
  providers: ProviderInfo[];
  isStreaming: boolean;
  mode?: string;
  onRetry?: (providerId: string) => Promise<void>;
  // When provided, prepended as the leftmost cell. Used for the v1.1 auto-
  // judge synthesis card (verdict.auto === true). Grid template grows to
  // accommodate (1 synthesis + N providers).
  synthesisCard?: ReactNode;
  /** Desktop-web tap-to-expand. When provided, each provider card's header
   *  becomes clickable and fires this with its providerId. */
  onProviderExpand?: (providerId: string) => void;
  // TTS read-aloud: when both `recordId` and `onReadAloud` are set, each
  // provider card renders a Read-aloud button. `ttsActiveProviderId` is
  // the providerId of the current TTS target on this record (if any),
  // so only that card shows the active-state styling.
  recordId?: string;
  onReadAloud?: (recordId: string, providerId: string, text: string) => void;
  ttsActiveProviderId?: string | null;
  ttsProgress?: number;
  // Per-provider cache-hit lookup. Returns true when the provider's
  // text audio is already cached server-side.
  isProviderTTSCached?: (providerId: string) => boolean;
}

export default function ResponseGrid({
  responses,
  providerOrder,
  providers,
  isStreaming,
  mode,
  onRetry,
  synthesisCard,
  onProviderExpand,
  recordId,
  onReadAloud,
  ttsActiveProviderId,
  ttsProgress,
  isProviderTTSCached,
}: ResponseGridProps) {
  const providerCount = providerOrder.length;
  const totalCols = providerCount + (synthesisCard ? 1 : 0);
  const columns = `repeat(${totalCols}, minmax(0, 1fr))`;

  return (
    <div className="response-grid" style={{ gridTemplateColumns: columns, alignItems: "start" }}>
      {synthesisCard}
      {providerOrder.map((providerId) => {
        // While the turn is in flight, an expected provider with no persisted
        // response yet renders a SPINNER placeholder card (not nothing) — so
        // history-revisit shows a stable skeleton instead of cards popping in
        // one by one. Once complete (not streaming), a missing provider is
        // still dropped (truly absent).
        const response =
          responses[providerId] ??
          (isStreaming
            ? { id: "", text: "", isComplete: false, wasInterrupted: false }
            : undefined);
        if (!response) return null;

        const provider = providers.find((p) => p.id === providerId);
        const name = provider?.display_name ?? providerId;
        const color = provider?.color ?? "var(--text-secondary)";
        const icon = provider?.icon;

        // Prefer the per-response mode set by the dispatcher (Auto-mode
        // populates it from classifier decisions, non-Auto from the
        // session-level mode). Falls back to a model-based reverse lookup
        // for older records persisted before per-response mode was tracked.
        // Reverse-lookup is ambiguous when one model serves multiple modes
        // (OpenAI uses gpt-5.5 for both fast and thinking — without an
        // explicit mode, find() would always return "fast"), so this
        // ordering matters.
        const derivedMode = provider?.models_by_mode && response.model
          ? Object.entries(provider.models_by_mode).find(([, m]) => m.model === response.model)?.[0]
          : undefined;
        const cardMode = response.mode
          || (mode === "auto" ? derivedMode : mode)
          || derivedMode;

        return (
          <ResponseCard
            key={providerId}
            providerId={providerId}
            providerName={name}
            providerColor={color}
            providerIcon={icon}
            response={response}
            isStreaming={isStreaming && !response.isComplete}
            mode={cardMode}
            onRetry={onRetry}
            onExpand={onProviderExpand ? () => onProviderExpand(providerId) : undefined}
            recordId={recordId}
            onReadAloud={onReadAloud}
            isTTSActive={ttsActiveProviderId === providerId}
            ttsProgress={ttsActiveProviderId === providerId ? ttsProgress : undefined}
            isTTSCached={isProviderTTSCached ? isProviderTTSCached(providerId) : undefined}
          />
        );
      })}
    </div>
  );
}
